// Données centralisées pour la bibliothèque de traitements
// Ces données sont utilisées pour l'analyse des symptômes

export interface TreatmentResource {
  id: string;
  name: string;
  category: 'aromatherapy' | 'gemmotherapy' | 'clay' | 'vegetable_oil' | 'hydrolat' | 'apitherapy';
  latinName?: string;
  keywords: string[];  // Mots-clés pour le matching avec les symptômes
  indications: string[];
  contraindications: string[];
  effects?: string;
  usage?: string;
  posology?: string;
}

// ==================== AROMATHÉRAPIE ====================
export const aromatherapyData: TreatmentResource[] = [
  {
    id: "marjolaine",
    name: "Marjolaine",
    latinName: "Origanum majorana",
    category: "aromatherapy",
    keywords: ["stress", "anxiété", "nervosité", "surmenage", "insomnie", "sommeil", "spasmes", "digestif", "tension", "musculaire", "respiratoire"],
    indications: ["Stress, anxiété, surmenage nerveux", "Insomnie", "Spasmes digestifs, dyspepsies", "Tensions musculaires", "Troubles respiratoires légers"],
    contraindications: ["Grossesse + allaitement", "Enfants < 6 ans", "Prudence en cas d'hypotension"],
    effects: "Neurotonique, rééquilibrant nerveux, anti-asthénique, stimulant digestif, antispasmodique, tonique circulatoire, antalgique",
    usage: "Usage interne: 1 goutte dans miel ou comprimé neutre, 2×/jour pendant 5 jours. Usage externe: Dilution 20% dans une huile végétale, massage plexus solaire, abdomen, nuque.",
    posology: "1 goutte 2×/jour max pendant 5 jours"
  },
  {
    id: "romarin",
    name: "Romarin",
    latinName: "Rosmarinus officinalis",
    category: "aromatherapy",
    keywords: ["bronchite", "respiratoire", "fatigue", "concentration", "foie", "hépatique", "digestion", "musculaire", "douleur", "mucus"],
    indications: ["Bronchite, encombrement respiratoire", "Fatigue mentale, baisse de concentration", "Troubles du foie, digestion lente", "Douleurs musculaires"],
    contraindications: ["Hypertension, épilepsie", "Grossesse et allaitement", "Enfants < 6 ans", "Pas en usage interne prolongé"],
    effects: "Mucolytique, expectorant, anti-infectieux, tonique musculaire, veineux et cérébral, régulateur hépatique, antalgique",
    usage: "Usage interne: 1 goutte 1×/jour max, 5 jours. Usage externe: Dilution 10–20%, massage du dos, thorax, jambes.",
    posology: "1 goutte 1×/jour max pendant 5 jours"
  },
  {
    id: "cypres",
    name: "Cyprès",
    latinName: "Cupressus sempervirens",
    category: "aromatherapy",
    keywords: ["varices", "jambes lourdes", "circulatoire", "toux", "hémorroïdes", "veineux", "circulation"],
    indications: ["Varices, jambes lourdes", "Toux sèche", "Hémorroïdes", "Troubles circulatoires"],
    contraindications: ["Mastoses, cancers hormonodépendants", "Grossesse", "Enfants < 12 ans"],
    effects: "Décongestionnant veineux, vasoconstricteur, cicatrisant, neurotonique, immunostimulant",
    usage: "Usage interne rare. Usage externe: Dilution 20–30%, massage des jambes.",
    posology: "Usage externe principalement, dilution 20-30%"
  },
  {
    id: "lavande",
    name: "Lavande vraie",
    latinName: "Lavandula vera",
    category: "aromatherapy",
    keywords: ["insomnie", "anxiété", "stress", "nerveux", "piqûre", "brûlure", "douleur", "musculaire", "dermatite", "prurit", "sommeil", "calme"],
    indications: ["Insomnie, anxiété, crise de nerfs", "Piqûres, brûlures légères", "Douleurs musculaires", "Dermatites, prurit"],
    contraindications: ["Aucune majeure", "Prudence hypotension"],
    effects: "Sédative, calmante, antiseptique, cicatrisante, décontractante, anti-inflammatoire",
    usage: "Usage interne: 1 goutte le soir. Usage externe: Pure sur piqûres ou brûlures, dilution 20% pour massages.",
    posology: "1 goutte le soir, ou application locale"
  },
  {
    id: "thym",
    name: "Thym vulgaire",
    latinName: "Thymus vulgaris",
    category: "aromatherapy",
    keywords: ["infection", "pulmonaire", "rhume", "grippe", "digestion", "fatigue", "intestinal", "immunité", "fièvre"],
    indications: ["Infections pulmonaires", "Rhume, grippe", "Digestion lente", "Fatigue sévère", "Infections intestinales"],
    contraindications: ["Enfants < 12 ans", "Grossesse, allaitement", "Hypertension", "Ulcère gastrique"],
    effects: "Anti-infectieux majeur, stimulant gastro-intestinal, immunostimulant, antalgique, drainant toxines",
    usage: "Usage interne: 1 goutte 1×/jour dans HV → 5 jours max. Usage externe: Dilution 10% max.",
    posology: "1 goutte 1×/jour max pendant 5 jours"
  },
  {
    id: "eucalyptus",
    name: "Eucalyptus globulus",
    latinName: "Eucalyptus globulus",
    category: "aromatherapy",
    keywords: ["bronchite", "rhume", "grippe", "sinusite", "respiratoire", "infection", "urinaire", "mucus", "toux"],
    indications: ["Bronchite", "Rhume, grippe", "Sinusite", "Infections urinaires"],
    contraindications: ["Enfants < 6 ans", "Asthme", "Épilepsie"],
    effects: "Expectorant, mucolytique, antibactérien, antiviral, décongestionnant",
    usage: "Usage interne: 1 goutte dans miel → 3 jours. Usage externe: Dilution 20%, massage thorax.",
    posology: "1 goutte dans miel, 3 jours max"
  },
  {
    id: "menthe",
    name: "Menthe poivrée",
    latinName: "Mentha piperita",
    category: "aromatherapy",
    keywords: ["migraine", "mal de tête", "nausée", "digestion", "fatigue", "douleur", "céphalée"],
    indications: ["Migraines", "Nausées, digestion lente", "Fatigue"],
    contraindications: ["Enfants < 6 ans", "Femmes enceintes", "Hypertension"],
    effects: "Analgésique, cholagogue, tonique général, anti-infectieux",
    usage: "Usage interne: 1 goutte dans miel. Usage externe: Dilution 10%, tempes (jamais yeux).",
    posology: "1 goutte dans miel, 2×/jour max"
  },
  {
    id: "geranium",
    name: "Géranium rosat",
    latinName: "Pelargonium graveolens",
    category: "aromatherapy",
    keywords: ["acné", "peau", "mycose", "arthrose", "dermatose", "cutané", "veineux"],
    indications: ["Acné, impétigo", "Dermatoses", "Mycoses", "Arthrose"],
    contraindications: ["Aucune majeure", "Possibles allergies cutanées"],
    effects: "Antifongique, microbicide, antalgique, astringent, tonique veineux",
    usage: "Usage interne: 1 goutte → 2×/jour. Usage externe: Dilution 10–20%.",
    posology: "1 goutte 2×/jour ou application locale diluée"
  },
  {
    id: "origan",
    name: "Origan",
    latinName: "Thymus capitatus",
    category: "aromatherapy",
    keywords: ["infection", "respiratoire", "intestinal", "colite", "parasitaire", "fongique", "mycose"],
    indications: ["Infections respiratoires sévères", "Infections intestinales", "Amibiases", "Colites"],
    contraindications: ["Très irritant", "Déconseillé pour enfants, grossesse", "Pas d'usage prolongé"],
    effects: "Anti-infectieux majeur, fongicide, parasiticide",
    usage: "Usage interne: 1 goutte dans HV → 3 jours. Usage externe: Dilution < 5%.",
    posology: "1 goutte dans huile végétale, 3 jours max"
  },
  {
    id: "basilic",
    name: "Basilic",
    latinName: "Ocimum basilicum",
    category: "aromatherapy",
    keywords: ["colique", "spasme", "digestif", "stress", "hépatique", "ballonnement"],
    indications: ["Coliques, spasmes digestifs", "Stress", "Troubles hépatiques légers"],
    contraindications: ["Grossesse", "Enfants", "Irritant pur"],
    effects: "Antispasmodique puissant, anti-inflammatoire, décongestionnant, sédatif",
    usage: "Usage interne: 1 goutte → 2×/jour, 5 jours. Usage externe: Dilution 5–10%, abdomen.",
    posology: "1 goutte 2×/jour pendant 5 jours"
  },
  {
    id: "petitgrain",
    name: "Oranger amer — Petit grain bigarade",
    latinName: "Citrus aurantium",
    category: "aromatherapy",
    keywords: ["stress", "anxiété", "sommeil", "insomnie", "peau", "cutané", "calme", "nerveux"],
    indications: ["Stress, anxiété", "Troubles du sommeil", "Affections cutanées"],
    contraindications: ["Aucune majeure", "Prudence hypotension"],
    effects: "Calmante, sédative, spasmolytique, anti-inflammatoire, cutanée",
    usage: "Usage interne: 1 goutte le soir. Usage externe: Dilution 20%, poignets, plexus.",
    posology: "1 goutte le soir ou massage dilué"
  },
  {
    id: "girofle",
    name: "Clou de girofle",
    latinName: "Eugenia caryophyllata",
    category: "aromatherapy",
    keywords: ["dentaire", "douleur", "infection", "intestinal", "mycose", "diarrhée", "antalgique"],
    indications: ["Névralgies dentaires", "Infections intestinales", "Mycoses", "Hépatites virales", "Diarrhées infectieuses"],
    contraindications: ["Irritante, dermocaustique", "Grossesse/allaitement", "Ulcères gastriques"],
    effects: "Bactéricide puissant, antivirale, antiparasitaire, antalgique puissant, stimulante générale",
    usage: "Usage interne: 1 goutte dans HV → 2 jours max. Usage externe: Dilution 2%, local dentaire.",
    posology: "1 goutte dans huile végétale, 2 jours max"
  },
  {
    id: "sarriette",
    name: "Sarriette des montagnes",
    latinName: "Satureja montana",
    category: "aromatherapy",
    keywords: ["infection", "intestinal", "parasitose", "mycose", "fatigue", "sexuel", "intellectuel", "antifongique"],
    indications: ["Infections intestinales", "Parasitoses", "Mycoses", "Fatigue sexuelle / intellectuelle"],
    contraindications: ["Interdite pure", "Grossesse, allaitement", "Enfants", "Irritante → prudence peau"],
    effects: "Anti-infectieux majeur, antifongique, antiparasitaire, tonique nerveux, antalgique",
    usage: "Usage interne: 1 goutte dans HV → 3 jours max. Usage externe: Dilution 5% max.",
    posology: "1 goutte dans huile végétale, 3 jours max"
  },
  {
    id: "sauge-off",
    name: "Sauge officinale",
    latinName: "Salvia officinalis",
    category: "aromatherapy",
    keywords: ["sueur", "hormonal", "ORL", "digestif", "hépatique", "ménopause", "sudation"],
    indications: ["Sueurs excessives", "Troubles hormonaux", "Infections ORL", "Troubles digestifs d'origine hépatique"],
    contraindications: ["⚠️ FORMELLE : Grossesse", "⚠️ FORMELLE : Allaitement", "⚠️ FORMELLE : Enfants", "⚠️ FORMELLE : Cancer hormone-dépendant", "Interdite en interne sans supervision (cétones → neurotoxicité)"],
    effects: "Emménagogue, régulateur hormonal, mucolytique, microbicide, régulateur de la sudation, cholagogue + cholérétique",
    usage: "Usage interne: Généralement interdite. Usage externe: Dilution stricte 5–10%.",
    posology: "Usage très encadré, dilution 5-10% max"
  },
  {
    id: "cannelle",
    name: "Cannelle",
    latinName: "Cinnamomum cassia/ceylon",
    category: "aromatherapy",
    keywords: ["grippe", "fièvre", "infection", "digestif", "fatigue", "antiviral"],
    indications: ["Grippe, fièvres", "Infections digestives", "Amibiase", "Fatigue sévère"],
    contraindications: ["Dermocaustique", "Interdite grossesse", "Enfants", "Ulcères"],
    effects: "Anti-infectieux, antivirale, antifongique, stimulante",
    usage: "Usage interne: 1 goutte dans HV → 2 jours. Usage externe: Dilution 1–2% max.",
    posology: "1 goutte dans huile végétale, 2 jours max"
  },
  {
    id: "myrte",
    name: "Myrte",
    latinName: "Myrtus communis",
    category: "aromatherapy",
    keywords: ["bronchite", "sinusite", "angine", "peau", "ride", "respiratoire"],
    indications: ["Bronchites, sinusites", "Peaux ridées", "Angines"],
    contraindications: ["Grossesse", "Enfants < 6 ans"],
    effects: "Expectorante, anticatarrhale, anti-infectieuse, hémostatique",
    usage: "Usage interne: 1 goutte → 3 jours max. Usage externe: Dilution 20%.",
    posology: "1 goutte, 3 jours max ou massage dilué"
  },
  {
    id: "pin",
    name: "Pin sylvestre",
    latinName: "Pinus sylvestris",
    category: "aromatherapy",
    keywords: ["bronchite", "cystite", "urinaire", "fatigue", "asthénie", "respiratoire"],
    indications: ["Bronchites", "Cystites", "Asthénie"],
    contraindications: ["Hypertension", "Grossesse", "Irritant pur"],
    effects: "Antiseptique respiratoire, stimulant surrénalien, antiseptique urinaire",
    usage: "Usage interne: 1 goutte → 2×/jour. Usage externe: Dilution 20%.",
    posology: "1 goutte 2×/jour ou massage dilué"
  },
  {
    id: "carotte",
    name: "Carotte",
    latinName: "Daucus carota",
    category: "aromatherapy",
    keywords: ["peau", "hépatique", "foie", "vieillissement", "régénération", "cutané"],
    indications: ["Peau terne, vieillissante", "Fatigue hépatique", "Régénération cellulaire"],
    contraindications: ["Grossesse", "Vitamine A excessive (prudence)"],
    effects: "Stimulant hépatorénal, régénérant cutané, anti-anémique",
    usage: "Usage interne: 1 goutte/j dans HV. Usage externe: Dilution 10%.",
    posology: "1 goutte/jour ou application diluée"
  },
  {
    id: "serpolet",
    name: "Serpolet",
    latinName: "Thymus serpyllum",
    category: "aromatherapy",
    keywords: ["entérocolite", "flatulence", "gastralgie", "urinaire", "digestif", "infection"],
    indications: ["Entérocolites", "Flatulences", "Gastralgies", "Infections urinaires hautes"],
    contraindications: ["Grossesse", "Enfants", "Irritant pur"],
    effects: "Anti-infectieux moyen, antiviral, bactéricide, stomachique, neurotonique",
    usage: "Usage interne: 1 goutte → digestion. Usage externe: Dilution 5–10%.",
    posology: "1 goutte pour digestion ou massage dilué"
  }
];

// ==================== GEMMOTHÉRAPIE ====================
export const gemmotherapyData: TreatmentResource[] = [
  {
    id: "aubepine",
    name: "Aubépine",
    latinName: "Crataegus oxyacantha",
    category: "gemmotherapy",
    keywords: ["hypertension", "tachycardie", "anxiété", "sommeil", "palpitation", "cardiaque", "coeur", "nerveux"],
    indications: ["Hypertension", "Tachycardie", "Anxiété", "Troubles du sommeil", "Palpitations"],
    contraindications: [],
    effects: "Régulateur cardiaque, apaisant nerveux, hypotenseur",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "bouleau-verruqueux",
    name: "Bouleau verruqueux",
    latinName: "Betula verrucosa",
    category: "gemmotherapy",
    keywords: ["rhumatisme", "allergie", "drainage", "acidose", "détox", "foie", "reins", "inflammation"],
    indications: ["Rhumatisme", "Allergies", "Drainage printanier", "Acidose", "Détox foie-reins"],
    contraindications: [],
    effects: "Drainage général, anti-inflammatoire, reminéralisant",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "cassis",
    name: "Cassis",
    latinName: "Ribes nigrum",
    category: "gemmotherapy",
    keywords: ["allergie", "inflammation", "arthrite", "asthme", "immunité", "cortisone", "antihistaminique"],
    indications: ["Allergies", "Inflammations", "Arthrite", "Asthme", "Immunité faible"],
    contraindications: ["Hypertension (avec prudence)"],
    effects: "Anti-inflammatoire majeur, cortison-like naturel, antihistaminique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "figuier",
    name: "Figuier",
    latinName: "Ficus carica",
    category: "gemmotherapy",
    keywords: ["stress", "gastrite", "reflux", "anxiété", "digestif", "sommeil", "nerveux", "estomac", "ulcère"],
    indications: ["Stress", "Gastrite", "Reflux", "Anxiété digestive", "Troubles du sommeil"],
    contraindications: [],
    effects: "Régulateur nerveux, antispasmodique digestif, anti-ulcéreux",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "genevrier",
    name: "Genévrier",
    latinName: "Juniperus communis",
    category: "gemmotherapy",
    keywords: ["hépatique", "foie", "dyspepsie", "digestion", "détox", "rénal", "antiseptique"],
    indications: ["Insuffisance hépatique", "Dyspepsie", "Digestion lente", "Cure dépurative"],
    contraindications: [],
    effects: "Détox hépatique & rénale, antiseptique, stimulant digestif",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "tilleul",
    name: "Tilleul",
    latinName: "Tilia tomentosa",
    category: "gemmotherapy",
    keywords: ["stress", "insomnie", "anxiété", "agitation", "spasme", "digestif", "nerveux", "sommeil", "calme"],
    indications: ["Stress", "Insomnie", "Anxiété", "Agitation", "Spasmes digestifs"],
    contraindications: [],
    effects: "Sédatif, régulateur nerveux, antispasmodique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "romarin-gemmo",
    name: "Romarin",
    latinName: "Rosmarinus officinalis",
    category: "gemmotherapy",
    keywords: ["hépatique", "foie", "cholestérol", "concentration", "mémoire", "détox", "fatigue"],
    indications: ["Fatigue hépatique", "Cholestérol", "Manque de concentration"],
    contraindications: ["Hypertension (prudence)"],
    effects: "Hépatoprotecteur, détoxifiant, stimulant cérébral",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "noisetier",
    name: "Noisetier",
    latinName: "Corylus avellana",
    category: "gemmotherapy",
    keywords: ["fibrose", "respiratoire", "pulmonaire", "circulatoire", "poumon"],
    indications: ["Fibrose", "Insuffisance respiratoire", "Troubles circulatoires"],
    contraindications: [],
    effects: "Anti-fibrosant, stimulant circulatoire, soutien pulmonaire",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "noyer",
    name: "Noyer",
    latinName: "Juglans regia",
    category: "gemmotherapy",
    keywords: ["dysbiose", "diarrhée", "peau", "mycose", "intestinal", "flore", "cutané"],
    indications: ["Dysbiose", "Diarrhée chronique", "Maladies de peau", "Mycoses"],
    contraindications: [],
    effects: "Antiseptique intestinal, régénération flore, purifiant",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "eglantier",
    name: "Églantier",
    latinName: "Rosa canina",
    category: "gemmotherapy",
    keywords: ["otite", "rhinite", "angine", "ORL", "immunité", "enfant"],
    indications: ["Otites", "Rhinites", "Angines", "ORL chroniques enfant/adulte"],
    contraindications: [],
    effects: "Stimulant immunitaire enfant, anti-inflammatoire ORL",
    posology: "1 goutte par année d'âge pour les enfants, 5-15 gouttes pour adultes"
  },
  {
    id: "frene",
    name: "Frêne",
    latinName: "Fraxinus excelsior",
    category: "gemmotherapy",
    keywords: ["arthrite", "goutte", "œdème", "articulaire", "rhumatisme", "acide urique", "diurétique"],
    indications: ["Arthrite", "Goutte", "Œdèmes", "Surcharges articulaires"],
    contraindications: [],
    effects: "Antirhumatismal, drainant acide urique, diurétique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "marronnier",
    name: "Marronnier",
    latinName: "Aesculus hippocastanum",
    category: "gemmotherapy",
    keywords: ["varices", "hémorroïdes", "jambes lourdes", "veineux", "œdème", "circulatoire"],
    indications: ["Varices", "Hémorroïdes", "Jambes lourdes"],
    contraindications: [],
    effects: "Tonique veineux, anti-œdème, décongestionnant",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "vigne",
    name: "Vigne",
    latinName: "Vitis vinifera",
    category: "gemmotherapy",
    keywords: ["arthrose", "polyarthrite", "inflammation", "articulaire", "chronique"],
    indications: ["Arthrose", "Polyarthrite", "Maladies inflammatoires chroniques"],
    contraindications: [],
    effects: "Anti-inflammatoire articulaire, anti-oxydant, anti-hyperplasique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "sequoia",
    name: "Séquoia",
    latinName: "Sequoia gigantea",
    category: "gemmotherapy",
    keywords: ["fatigue", "libido", "ostéoporose", "masculin", "vieillissement", "tonique", "sexuel"],
    indications: ["Fatigue", "Perte de libido", "Ostéoporose", "Vieillissement masculin"],
    contraindications: ["Grossesse (bourgeon hormonal)"],
    effects: "Tonique général masculin, reminéralisant, stimulant sexuel",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "chene",
    name: "Chêne",
    latinName: "Quercus robur",
    category: "gemmotherapy",
    keywords: ["burn-out", "asthénie", "hypotension", "fatigue", "épuisement", "tonique"],
    indications: ["Burn-out", "Asthénie", "Hypotension"],
    contraindications: ["Hypertension"],
    effects: "Tonique général, stimulant endocrinien, contre la fatigue profonde",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "framboisier",
    name: "Framboisier",
    latinName: "Rubus idaeus",
    category: "gemmotherapy",
    keywords: ["prémenstruel", "cycle", "ménopause", "hormonal", "féminin", "règles"],
    indications: ["Syndrome prémenstruel", "Cycles irréguliers", "Ménopause"],
    contraindications: ["Grossesse (bourgeon hormonal)"],
    effects: "Régulateur hormonal féminin",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "airelle",
    name: "Airelle",
    latinName: "Vaccinium vitis-idaea",
    category: "gemmotherapy",
    keywords: ["cystite", "urinaire", "hormonal", "transit", "progestérone"],
    indications: ["Cystites", "Troubles hormonaux", "Transit paresseux"],
    contraindications: [],
    effects: "Antiseptique urinaire, progestérone-like",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "myrtillier",
    name: "Myrtillier",
    latinName: "Vaccinium myrtillus",
    category: "gemmotherapy",
    keywords: ["rétinopathie", "oculaire", "yeux", "vue", "capillaire", "diabète", "vasculaire"],
    indications: ["Rétinopathies", "Fragilité capillaire", "Diabète"],
    contraindications: [],
    effects: "Protection vasculaire et oculaire",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "orme",
    name: "Orme",
    latinName: "Ulmus campestris",
    category: "gemmotherapy",
    keywords: ["eczéma", "psoriasis", "peau", "cutané", "drainage", "dépuratif"],
    indications: ["Eczéma", "Psoriasis", "Peau chargée"],
    contraindications: [],
    effects: "Drainage cutané, dépuratif dermique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "olivier",
    name: "Olivier",
    latinName: "Olea europaea",
    category: "gemmotherapy",
    keywords: ["hypertension", "cholestérol", "cardiovasculaire", "tension"],
    indications: ["Hypertension", "Cholestérol"],
    contraindications: [],
    effects: "Hypotenseur, protecteur cardiovasculaire",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "sapin-pectine",
    name: "Sapin pectiné",
    latinName: "Abies pectinata",
    category: "gemmotherapy",
    keywords: ["ostéoporose", "croissance", "fracture", "os", "osseux", "reminéralisant"],
    indications: ["Ostéoporose", "Retard de croissance", "Fractures"],
    contraindications: [],
    effects: "Ossification, reminéralisant, croissance",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "saule",
    name: "Saule",
    latinName: "Salix alba",
    category: "gemmotherapy",
    keywords: ["fièvre", "douleur", "arthrose", "grippe", "inflammation", "antalgique"],
    indications: ["Fièvre", "Douleurs", "Arthrose", "États grippaux"],
    contraindications: ["Allergie aux salicylates"],
    effects: "Anti-inflammatoire, antalgique, fébrifuge (salicylates naturels)",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "viorne",
    name: "Viorne",
    latinName: "Viburnum lantana",
    category: "gemmotherapy",
    keywords: ["asthme", "bronchiolite", "toux", "respiratoire", "spasme"],
    indications: ["Asthme", "Bronchiolite", "Toux spasmodiques"],
    contraindications: [],
    effects: "Antispasmodique respiratoire",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "ronce",
    name: "Ronce",
    latinName: "Rubus fruticosus",
    category: "gemmotherapy",
    keywords: ["fibrose pulmonaire", "ostéoporose", "poumon", "os", "régénérant"],
    indications: ["Fibrose pulmonaire", "Ostéoporose"],
    contraindications: [],
    effects: "Régénérant pulmonaire et osseux",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "aulne-glutineux",
    name: "Aulne glutineux",
    latinName: "Alnus glutinosa",
    category: "gemmotherapy",
    keywords: ["migraine", "sinusite", "phlébite", "vasculaire", "cérébral"],
    indications: ["Migraines", "Sinusites chroniques", "Phlébite (prévention)"],
    contraindications: [],
    effects: "Anti-inflammatoire vasculaire, stimulant cérébral",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "peuplier",
    name: "Peuplier",
    latinName: "Populus nigra",
    category: "gemmotherapy",
    keywords: ["artérite", "circulatoire", "varices", "sanguin", "vasculaire"],
    indications: ["Artérite", "Troubles circulatoires", "Varices douloureuses"],
    contraindications: [],
    effects: "Anti-inflammatoire vasculaire, fluidifiant sanguin",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "sorbier",
    name: "Sorbier",
    latinName: "Sorbus domestica",
    category: "gemmotherapy",
    keywords: ["œdème", "jambes lourdes", "rétention", "lymphatique", "veineux"],
    indications: ["Œdèmes", "Jambes lourdes", "Rétention d'eau"],
    contraindications: [],
    effects: "Tonique veineux et lymphatique",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "hetre",
    name: "Hêtre",
    latinName: "Fagus sylvatica",
    category: "gemmotherapy",
    keywords: ["immunité", "rétention", "œdème", "défense"],
    indications: ["Déficience immunitaire", "Rétention d'eau"],
    contraindications: [],
    effects: "Stimulant immunitaire, anti-œdème",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "chevrefeuille",
    name: "Chèvrefeuille",
    latinName: "Lonicera nigra",
    category: "gemmotherapy",
    keywords: ["rhinite", "sinusite", "ORL", "respiratoire"],
    indications: ["Rhinite", "Sinusite chronique"],
    contraindications: [],
    effects: "Anti-inflammatoire ORL",
    posology: "5 à 15 gouttes par jour"
  },
  {
    id: "pommier",
    name: "Pommier",
    latinName: "Malus domestica",
    category: "gemmotherapy",
    keywords: ["stress", "spasme", "digestif", "émotionnel", "nerveux"],
    indications: ["Stress", "Spasmes digestifs", "Troubles émotionnels"],
    contraindications: [],
    effects: "Régulateur nerveux, adoucissant digestif",
    posology: "5 à 15 gouttes par jour"
  }
];

// ==================== ARGILE ====================
export const clayData: TreatmentResource[] = [
  {
    id: "argile-verte",
    name: "Argile verte",
    category: "clay",
    keywords: ["peau grasse", "acné", "purifiante", "détox", "cataplasme", "douleur", "entorse", "rhumatisme", "abcès", "plaie"],
    indications: ["Peaux grasses", "Acné", "Douleurs musculaires", "Entorses", "Rhumatismes", "Abcès", "Plaies"],
    contraindications: ["Femme enceinte / allaitante (usage interne)", "Hernie / occlusion intestinale", "Médicaments, contraceptifs oraux", "Hypertension", "Sur agrafes, points, prothèses"],
    effects: "Purifiante, absorbante, reminéralisante, cicatrisante",
    usage: "Usage externe: Cataplasme 2cm épaisseur, 1h à toute une nuit. Usage interne: 1 c. à café dans un verre d'eau le soir, cure 3 semaines.",
    posology: "Cataplasme externe ou 1 c. à café/jour en interne"
  },
  {
    id: "argile-blanche",
    name: "Argile blanche (Kaolin)",
    category: "clay",
    keywords: ["peau sensible", "bébé", "doux", "cicatrisant", "apaisant", "irritation"],
    indications: ["Peaux sensibles", "Bébés", "Irritations cutanées", "Érythèmes"],
    contraindications: ["Mêmes précautions que l'argile verte pour usage interne"],
    effects: "Ultra douce, cicatrisante, apaisante",
    usage: "Usage externe principalement, très doux pour les peaux fragiles",
    posology: "Application locale en cataplasme léger"
  },
  {
    id: "argile-rouge",
    name: "Argile rouge",
    category: "clay",
    keywords: ["peau mature", "peau sensible", "circulation", "tonifiante", "fer"],
    indications: ["Peaux matures", "Peaux sensibles", "Troubles circulatoires"],
    contraindications: ["Mêmes précautions que l'argile verte"],
    effects: "Tonifiante, riche en fer, stimule la circulation",
    usage: "Masque visage, cataplasme",
    posology: "Application locale"
  },
  {
    id: "argile-jaune",
    name: "Argile jaune",
    category: "clay",
    keywords: ["peau normale", "peau mixte", "équilibrante"],
    indications: ["Peaux normales à mixtes", "Rééquilibrage cutané"],
    contraindications: ["Mêmes précautions que l'argile verte"],
    effects: "Équilibrante, purifiante modérée",
    usage: "Masque visage",
    posology: "Application locale"
  },
  {
    id: "rhassoul",
    name: "Rhassoul",
    category: "clay",
    keywords: ["cheveux", "lavant", "shampooing", "corps", "moussant", "nettoyant"],
    indications: ["Lavage corps et cheveux", "Alternative aux shampoings", "Peaux grasses"],
    contraindications: ["Usage externe uniquement"],
    effects: "Lavant naturel, moussant, purifiant",
    usage: "En shampooing ou gel lavant, mélangé à l'eau",
    posology: "Mélanger à l'eau tiède, appliquer et rincer"
  },
  {
    id: "argile-usage-interne",
    name: "Argile (Usage interne)",
    category: "clay",
    keywords: ["acidité", "diarrhée", "infection intestinale", "ostéoporose", "digestif", "reminéralisant", "détox"],
    indications: ["Acidité gastrique", "Diarrhée", "Infection intestinale", "Ostéoporose", "Détoxification"],
    contraindications: ["Femme enceinte / allaitante", "Hernie / occlusion intestinale / constipation", "Médicaments, contraceptifs oraux", "Hypertension / huile de paraffine / régime gras"],
    effects: "Reminéralisante, absorbante, purifiante pour le système digestif",
    usage: "Eau d'argile: 1 c. à café dans un verre d'eau le soir, boire le matin sans remuer (reminéralisante) ou en remuant (complète). Cure: 3 semaines + pause 8 jours.",
    posology: "1 c. à café le soir, boire le matin"
  }
];

// ==================== HUILES VÉGÉTALES ====================
export const vegetableOilsData: TreatmentResource[] = [
  // === HUILES VÉGÉTALES ===
  {
    id: "amande-douce",
    name: "Huile d'Amande Douce",
    category: "vegetable_oil",
    keywords: ["peau sensible", "bébé", "vergeture", "hydratant", "démaquillant", "croûte de lait", "adoucissant", "nourrissant", "apaisant", "émollient", "vitamine A", "vitamine E"],
    indications: ["Bébés (croûtes de lait, peau sensible)", "Prévention des vergetures", "Démaquillant doux", "Peaux sèches et sensibles"],
    contraindications: ["Allergie aux fruits à coque"],
    effects: "Adoucissante, nourrissante, apaisante, émolliente, riche en vitamines A et E",
    usage: "Application pure ou comme support pour huiles essentielles",
    posology: "Application locale quotidienne"
  },
  {
    id: "argan",
    name: "Huile d'Argan",
    category: "vegetable_oil",
    keywords: ["anti-âge", "peau mature", "eczéma", "cheveux cassants", "vergeture", "gerçure", "régénérant", "réparateur", "hydratant", "acides gras essentiels"],
    indications: ["Peaux matures, irritées, eczéma", "Cheveux cassants", "Gerçures, vergetures, dermatoses"],
    contraindications: ["Aucune majeure"],
    effects: "Régénérante, hydratante, anti-âge, riche en acides gras essentiels",
    usage: "Application pure sur peau ou cheveux",
    posology: "Application locale quotidienne"
  },
  {
    id: "avocat",
    name: "Huile d'Avocat",
    category: "vegetable_oil",
    keywords: ["cheveux secs", "cheveux cassants", "cellulite", "peau terne", "élasticité", "régénérant", "nourrissant", "hydratant", "acides gras", "vitamines"],
    indications: ["Cheveux secs, cassants", "Anti-cellulite", "Peau terne, perte d'élasticité"],
    contraindications: ["Aucune majeure"],
    effects: "Nourrissante, hydratante, riche en acides gras et vitamines",
    usage: "Application pure ou en mélange",
    posology: "Application locale, massage"
  },
  {
    id: "calophylle",
    name: "Huile de Calophylle",
    category: "vegetable_oil",
    keywords: ["anti-inflammatoire", "circulatoire", "cicatrisante", "peau", "varices", "couperose", "jambes lourdes", "cicatrice", "acné", "rhumatisme"],
    indications: ["Problèmes circulatoires (varices, jambes lourdes)", "Couperose", "Cicatrices", "Acné", "Douleurs rhumatismales"],
    contraindications: ["Éviter chez les personnes sous anticoagulants"],
    effects: "Anti-inflammatoire, circulatoire, cicatrisante, utilisée pour les problèmes de peau",
    usage: "Application locale sur zones concernées",
    posology: "Application locale 2-3 fois par jour"
  },
  {
    id: "coco",
    name: "Huile de Coco",
    category: "vegetable_oil",
    keywords: ["hydratant", "nourrissant", "antibactérien", "antifongique", "cheveux", "peau", "démaquillant", "oil pulling", "mycose"],
    indications: ["Cheveux secs et abîmés", "Hydratation de la peau", "Soin buccal (oil pulling)", "Mycoses"],
    contraindications: ["Peut être comédogène sur certaines peaux"],
    effects: "Hydratante, nourrissante, antibactérienne, antifongique",
    usage: "Application sur peau ou cheveux, usage buccal",
    posology: "Application locale quotidienne"
  },
  {
    id: "nigelle",
    name: "Huile de Nigelle",
    category: "vegetable_oil",
    keywords: ["anti-inflammatoire", "allergie", "immunité", "acné", "eczéma", "psoriasis", "cheveux", "bronchodilatateur", "antioxydant", "antibactérien"],
    indications: ["Acné, eczéma, psoriasis", "Chute de cheveux", "Allergies", "Soutien immunitaire", "Problèmes de peau"],
    contraindications: ["Grossesse (usage interne)"],
    effects: "Antioxydante, anti-inflammatoire, antibactérienne, utilisée pour les problèmes de peau et le système immunitaire",
    usage: "Usage interne (salades) et externe (cataplasmes, soins)",
    posology: "1 c. à café/jour en interne ou application locale"
  },
  {
    id: "jojoba",
    name: "Huile de Jojoba",
    category: "vegetable_oil",
    keywords: ["peau grasse", "peau mixte", "peau sèche", "sébum", "cheveux", "anti-âge", "hydratant", "équilibrant", "régulateur"],
    indications: ["Peaux grasses, mixtes ou sèches", "Cheveux gras ou secs", "Régulation du sébum", "Tous types de peau"],
    contraindications: ["Aucune majeure"],
    effects: "Équilibrante, régulatrice du sébum, nourrissante, adaptée à tous types de peau",
    usage: "Application pure comme soin ou support pour HE",
    posology: "Application locale quotidienne"
  },
  {
    id: "macadamia",
    name: "Huile de Macadamia",
    category: "vegetable_oil",
    keywords: ["régénérant", "assouplissant", "acide palmitoléique", "peau mature", "vergeture", "cicatrice", "cheveux", "massage"],
    indications: ["Peaux matures", "Vergetures", "Cicatrices", "Cheveux secs", "Massages"],
    contraindications: ["Allergie aux fruits à coque"],
    effects: "Régénérante, assouplissante, riche en acide palmitoléique",
    usage: "Application pure ou en massage",
    posology: "Application locale quotidienne"
  },
  {
    id: "neem",
    name: "Huile de Neem",
    category: "vegetable_oil",
    keywords: ["antibactérien", "antifongique", "purifiant", "peau", "acné", "mycose", "psoriasis", "eczéma", "pellicules", "parasites"],
    indications: ["Acné", "Mycoses cutanées", "Psoriasis", "Eczéma", "Pellicules", "Parasites"],
    contraindications: ["Éviter chez la femme enceinte", "Usage externe uniquement"],
    effects: "Antibactérienne, antifongique, purifiante, utilisée pour les problèmes de peau",
    usage: "Application locale diluée (odeur forte)",
    posology: "Application locale, diluer dans une autre huile végétale"
  },
  {
    id: "noisette",
    name: "Huile de Noisette",
    category: "vegetable_oil",
    keywords: ["pénétrant", "astringent", "sébum", "peau grasse", "peau mixte", "acné", "massage", "vergeture"],
    indications: ["Peaux grasses ou mixtes", "Acné", "Massages (pénètre rapidement)", "Vergetures"],
    contraindications: ["Allergie aux fruits à coque"],
    effects: "Pénétrante, astringente, régulatrice du sébum, adaptée aux peaux grasses",
    usage: "Application locale, excellent support de massage",
    posology: "Application locale quotidienne"
  },
  {
    id: "onagre",
    name: "Huile d'Onagre",
    category: "vegetable_oil",
    keywords: ["hormonal", "anti-inflammatoire", "ménopause", "SPM", "peau sèche", "eczéma", "vieillissement cutané", "syndrome prémenstruel"],
    indications: ["Troubles hormonaux", "SPM", "Ménopause", "Eczéma", "Peau sèche", "Vieillissement cutané"],
    contraindications: ["Éviter en cas d'épilepsie", "Prudence avec anticoagulants"],
    effects: "Régulatrice hormonale, anti-inflammatoire, utilisée pour les problèmes hormonaux",
    usage: "Usage interne (capsules) et externe",
    posology: "1-2 capsules/jour ou application locale"
  },
  {
    id: "pepin-raisin",
    name: "Huile de Pépin de Raisin",
    category: "vegetable_oil",
    keywords: ["antioxydant", "régénérant", "raffermissant", "peau", "anti-âge", "léger", "non gras", "tous types de peau"],
    indications: ["Tous types de peau", "Peaux matures", "Soins anti-âge", "Raffermissement cutané"],
    contraindications: ["Aucune majeure"],
    effects: "Antioxydante, régénérante, raffermissante, adaptée à tous types de peau",
    usage: "Application locale, pénètre rapidement",
    posology: "Application locale quotidienne"
  },
  {
    id: "ricin",
    name: "Huile de Ricin",
    category: "vegetable_oil",
    keywords: ["cheveux", "cils", "ongles", "fortifiant", "cicatrisant", "émollient", "pousse cheveux", "sourcils"],
    indications: ["Fortifie cheveux, cils, sourcils, ongles", "Favorise la pousse des cheveux", "Cicatrisation"],
    contraindications: ["Usage externe uniquement", "Très visqueuse, à diluer"],
    effects: "Fortifiante des cheveux, cicatrisante, émolliente",
    usage: "Application locale sur cheveux, cils, ongles",
    posology: "Application locale, laisser poser avant shampoing"
  },
  {
    id: "rose-musquee",
    name: "Huile de Rose Musquée",
    category: "vegetable_oil",
    keywords: ["régénérant", "cicatrisant", "antioxydant", "vitamine A", "vitamine C", "anti-âge", "vergeture", "cicatrice", "tache pigmentaire"],
    indications: ["Cicatrices", "Vergetures", "Taches pigmentaires", "Rides", "Peaux matures"],
    contraindications: ["Éviter sur peau acnéique (comédogène)"],
    effects: "Régénérante, cicatrisante, antioxydante, riche en vitamines A et C",
    usage: "Application locale sur zones concernées",
    posology: "Application locale matin et soir"
  },
  {
    id: "sesame",
    name: "Huile de Sésame",
    category: "vegetable_oil",
    keywords: ["antioxydant", "nourrissant", "sébum", "massage", "ayurveda", "peau sèche", "cheveux", "protection solaire légère"],
    indications: ["Massages ayurvédiques", "Peaux sèches", "Cheveux secs", "Protection solaire légère"],
    contraindications: ["Allergie au sésame"],
    effects: "Antioxydante, nourrissante, régulatrice du sébum",
    usage: "Application locale, massage",
    posology: "Application locale quotidienne"
  },
  {
    id: "tamanu",
    name: "Huile de Tamanu (Calophylle Inophyle)",
    category: "vegetable_oil",
    keywords: ["cicatrisant", "régénérant", "anti-inflammatoire", "peau", "cicatrice", "acné", "eczéma", "brûlure", "couperose"],
    indications: ["Cicatrices", "Acné", "Eczéma", "Brûlures légères", "Couperose", "Vergetures"],
    contraindications: ["Éviter chez les personnes sous anticoagulants"],
    effects: "Cicatrisante, régénérante, anti-inflammatoire, utilisée pour les problèmes de peau",
    usage: "Application locale sur zones concernées",
    posology: "Application locale 2-3 fois par jour"
  },
  {
    id: "tournesol",
    name: "Huile de Tournesol",
    category: "vegetable_oil",
    keywords: ["émollient", "adoucissant", "vitamine E", "peau sèche", "économique", "massage", "support"],
    indications: ["Peaux sèches", "Support pour huiles essentielles", "Massages", "Soins quotidiens"],
    contraindications: ["Aucune majeure"],
    effects: "Émolliente, adoucissante, riche en vitamine E",
    usage: "Application locale, excellent support économique",
    posology: "Application locale quotidienne"
  },
  // === MACÉRATS HUILEUX ===
  {
    id: "arnica-macerat",
    name: "Macérât d'Arnica",
    category: "vegetable_oil",
    keywords: ["anti-inflammatoire", "analgésique", "contusion", "douleur musculaire", "hématome", "bleu", "sport", "courbature", "entorse"],
    indications: ["Contusions", "Hématomes", "Douleurs musculaires", "Courbatures", "Entorses"],
    contraindications: ["Ne pas appliquer sur plaie ouverte", "Usage externe uniquement"],
    effects: "Anti-inflammatoire, analgésique, utilisé pour les contusions et les douleurs musculaires",
    usage: "Application locale sur zones douloureuses",
    posology: "Massage local 2-3 fois par jour"
  },
  {
    id: "calendula",
    name: "Macérât de Calendula",
    category: "vegetable_oil",
    keywords: ["peau sensible", "rougeur", "piqûre", "urticaire", "anti-inflammatoire", "calmant", "allergie", "bébé", "apaisant", "cicatrisant", "irritation"],
    indications: ["Peaux sensibles", "Rougeurs", "Piqûres", "Urticaire", "Irritations", "Bébés"],
    contraindications: ["Allergie aux astéracées"],
    effects: "Apaisant, cicatrisant, anti-inflammatoire, utilisé pour les problèmes de peau et les irritations",
    usage: "Application locale sur zones irritées",
    posology: "Application locale quotidienne"
  },
  {
    id: "carotte-macerat",
    name: "Macérât de Carotte",
    category: "vegetable_oil",
    keywords: ["soleil", "bronzage", "bonne mine", "antioxydant", "anti-rides", "anti-acné", "peau", "caroténoïdes", "teint éclatant", "régénérant"],
    indications: ["Préparation au soleil", "Effet bonne mine", "Anti-rides", "Anti-acné", "Teint terne"],
    contraindications: ["⚠️ Ne remplace pas une crème solaire !"],
    effects: "Régénérant cutané, donne un teint éclatant, riche en caroténoïdes",
    usage: "Application locale avant exposition ou comme soin quotidien",
    posology: "Application locale"
  },
  {
    id: "millepertuis-macerat",
    name: "Macérât de Millepertuis",
    category: "vegetable_oil",
    keywords: ["brûlure", "coup de soleil", "hémorroïdes", "prurit", "anti-inflammatoire", "décongestionnant", "antidépresseur", "apaisant", "blessure", "peau"],
    indications: ["Brûlures", "Coups de soleil", "Hémorroïdes", "Prurit", "Blessures mineures", "Soutien émotionnel (usage externe)"],
    contraindications: ["⚠️ Photosensibilisant : éviter soleil pendant 24h après usage"],
    effects: "Antidépresseur naturel, apaisant, utilisé pour les problèmes de peau et les blessures mineures",
    usage: "Application locale sur zones concernées",
    posology: "Application locale, éviter exposition solaire 24h"
  },
  {
    id: "camomille-romaine-macerat",
    name: "Macérât de Camomille Romaine",
    category: "vegetable_oil",
    keywords: ["apaisant", "anti-inflammatoire", "peau sensible", "irritation", "rougeur", "bébé", "allergie", "démangeaison", "calmant"],
    indications: ["Peaux sensibles", "Irritations", "Rougeurs", "Bébés", "Démangeaisons"],
    contraindications: ["Allergie aux astéracées"],
    effects: "Apaisant, anti-inflammatoire, adapté aux peaux sensibles",
    usage: "Application locale sur zones irritées",
    posology: "Application locale quotidienne"
  },
  {
    id: "rose-musquee-macerat",
    name: "Macérât de Rose Musquée",
    category: "vegetable_oil",
    keywords: ["régénérant", "cicatrisant", "anti-âge", "acides gras essentiels", "vergeture", "cicatrice", "ride", "tache", "peau mature"],
    indications: ["Cicatrices", "Vergetures", "Rides", "Taches pigmentaires", "Peaux matures"],
    contraindications: ["Éviter sur peau acnéique"],
    effects: "Régénérant cutané, cicatrisant, anti-âge, riche en acides gras essentiels",
    usage: "Application locale sur zones concernées",
    posology: "Application locale matin et soir"
  }
];

// ==================== HYDROLATS ====================
export const hydrolatsData: TreatmentResource[] = [
  {
    id: "hydrolat-camomille",
    name: "Hydrolat de Camomille",
    latinName: "Matricaria recutita",
    category: "hydrolat",
    keywords: ["colique", "oculaire", "yeux", "peau sensible", "bébé", "nourrisson", "dent", "calme", "nerveux", "irritation", "inflammatoire"],
    indications: ["Coliques", "Troubles oculaires", "Peaux sensibles", "Coliques du nourrisson", "Poussées dentaires"],
    contraindications: ["Allergie possible aux Astéracées"],
    effects: "Calmante, digestive, anti-inflammatoire",
    usage: "Usage interne: coliques, troubles oculaires. Usage externe: peaux sensibles, coliques du nourrisson, poussées dentaires",
    posology: "1 c. à café dans un verre d'eau (2-3×/jour) ou compresses"
  },
  {
    id: "hydrolat-fleur-oranger",
    name: "Hydrolat de Fleur d'Oranger",
    latinName: "Citrus aurantium amara",
    category: "hydrolat",
    keywords: ["sommeil", "stress", "ménopause", "coeur", "peau", "irrité", "burn-out", "émotionnel", "bébé", "calme", "joie", "anxiété", "insomnie"],
    indications: ["Sommeil", "Stress", "Ménopause", "Peau irritée", "Choc émotionnel", "Burn-out"],
    contraindications: [],
    effects: "Sédatif, antidépresseur, digestif",
    usage: "Usage interne: sommeil, stress, ménopause, cœur. Usage externe: peau irritée, choc émotionnel",
    posology: "1 c. à café dans un verre d'eau le soir ou brumisation"
  },
  {
    id: "hydrolat-menthe-poivree",
    name: "Hydrolat de Menthe Poivrée",
    latinName: "Mentha x piperita",
    category: "hydrolat",
    keywords: ["migraine", "nausée", "eczéma", "zona", "jambes lourdes", "soleil", "fatigue", "tonique", "rafraîchissant", "digestion", "mal de tête"],
    indications: ["Migraines", "Nausées", "Eczéma", "Zona", "Jambes lourdes", "Coups de soleil", "Peaux fatiguées"],
    contraindications: ["Éviter chez les enfants en bas âge", "Prudence femmes enceintes"],
    effects: "Tonique, rafraîchissant, antalgique, antiviral",
    usage: "Usage interne: migraines, nausées, eczéma, zona. Usage externe: jambes lourdes, coups de soleil",
    posology: "Cure: 5 jours/sem pendant 3 sem + pause 1 sem"
  },
  {
    id: "hydrolat-rose",
    name: "Hydrolat de Rose",
    latinName: "Rosa damascena",
    category: "hydrolat",
    keywords: ["peau", "cutané", "détente", "stress", "relaxant", "tonique", "visage", "sensible", "régénérant", "anti-âge"],
    indications: ["Détente", "Gestion du stress", "Tonique visage", "Peaux sensibles"],
    contraindications: [],
    effects: "Apaisant, tonifiant, régénérant cutané, relaxant émotionnellement",
    usage: "Usage interne: dilué dans l'eau pour détente. Usage externe: tonique visage, peaux sensibles",
    posology: "1 c. à café dans un verre d'eau ou application locale"
  },
  {
    id: "hydrolat-lavande",
    name: "Hydrolat de Lavande",
    latinName: "Lavandula angustifolia",
    category: "hydrolat",
    keywords: ["relaxant", "sommeil", "peau", "irritation", "calme", "antiseptique", "ambiance", "stress", "anxiété", "insomnie"],
    indications: ["Relaxation", "Sommeil", "Irritations cutanées"],
    contraindications: [],
    effects: "Apaisant, relaxant, antiseptique doux, anti-inflammatoire léger",
    usage: "Usage interne: très faible quantité pour relaxation. Usage externe: spray calmant, irritations cutanées",
    posology: "Brumisation d'ambiance ou 1 c. à café dans un verre d'eau"
  },
  {
    id: "hydrolat-hamamelis",
    name: "Hydrolat d'Hamamélis",
    latinName: "Hamamelis virginiana",
    category: "hydrolat",
    keywords: ["astringent", "peau grasse", "imperfection", "acné", "rasage", "tonique", "inflammatoire", "circulatoire"],
    indications: ["Peau grasse", "Imperfections", "Après rasage"],
    contraindications: ["Usage interne avec prudence"],
    effects: "Astringent, anti-inflammatoire, tonique cutané",
    usage: "Usage externe principalement: tonique peau grasse, compresses, après rasage",
    posology: "Application locale en tonique ou compresses"
  },
  {
    id: "hydrolat-bleuet",
    name: "Hydrolat de Bleuets",
    latinName: "Centaurea cyanus",
    category: "hydrolat",
    keywords: ["yeux", "oculaire", "fatigué", "irrité", "peau sensible", "décongestionnant", "compresse"],
    indications: ["Yeux fatigués", "Yeux irrités", "Peaux sensibles"],
    contraindications: [],
    effects: "Apaisant, décongestionnant, tonique",
    usage: "Usage interne: hydratation douce. Usage externe: compresses pour yeux, peaux sensibles",
    posology: "Compresses sur yeux fermés 10-15 min"
  },
  {
    id: "hydrolat-fenouil",
    name: "Hydrolat de Fenouil",
    latinName: "Foeniculum vulgare",
    category: "hydrolat",
    keywords: ["digestion", "ballonnement", "digestif", "carminatif", "spasme", "circulatoire", "estomac", "intestin"],
    indications: ["Digestion difficile", "Ballonnements"],
    contraindications: ["Éviter en cas de cancer hormonodépendant"],
    effects: "Digestif doux, carminatif, antispasmodique",
    usage: "Usage interne: dilué dans l'eau pour digestion et ballonnements. Usage externe: tonique circulatoire",
    posology: "1 c. à soupe dans 1L d'eau à boire dans la journée"
  }
];

// ==================== APITHÉRAPIE ====================
export const apitherapyData: TreatmentResource[] = [
  {
    id: "miel",
    name: "Miel",
    category: "apitherapy",
    keywords: ["énergie", "fatigue", "immunité", "infection", "cicatrice", "plaie", "brûlure", "digestif", "constipation", "toux", "gorge", "sommeil", "peau", "antiseptique"],
    indications: ["Fatigue, manque d'énergie", "Infections respiratoires, toux", "Cicatrisation des plaies et brûlures", "Troubles digestifs, constipation", "Troubles du sommeil (miel de fleur d'oranger)", "Soins de la peau"],
    contraindications: ["Diabète (modération)", "Enfants < 1 an (risque botulisme)"],
    effects: "Dynamisant, antioxydant, antiseptique, anti-inflammatoire, sédatif (le soir), antibiotique naturel, cicatrisant, digestif, cardioprotecteur",
    usage: "Pur en cuillère, dans tisane ou sur pansement pour cicatrisation",
    posology: "1 à 2 cuillères à soupe par jour"
  },
  {
    id: "propolis",
    name: "Propolis",
    category: "apitherapy",
    keywords: ["infection", "rhume", "grippe", "angine", "gorge", "toux", "immunité", "mycose", "cicatrice", "foie", "virus", "bactérie", "hiver"],
    indications: ["Infections hivernales : rhumes, angines, grippe, gastro-entérites", "Maux de gorge", "Mycoses cutanées", "Cicatrisation", "Protection hépatique"],
    contraindications: ["Allergie aux produits de la ruche"],
    effects: "Antibiotique naturel (bactériostatique, antivirale), antifongique, immunostimulant, cicatrisant, anesthésiant local, détoxifiant hépatique",
    usage: "Teinture-mère, spray buccal/nasal, pastilles, sirops, pommades",
    posology: "En cure de 3 semaines, selon forme utilisée"
  },
  {
    id: "pollen",
    name: "Pollen",
    category: "apitherapy",
    keywords: ["fatigue", "déprime", "énergie", "digestif", "transit", "intestin", "flore", "prostate", "cheveux", "vieillissement", "croissance", "os"],
    indications: ["Fatigue physique et mentale, déprime", "Troubles digestifs, déséquilibre de la flore intestinale", "Troubles de la prostate", "Chute de cheveux", "Soutien pendant la croissance"],
    contraindications: ["Allergie au pollen"],
    effects: "Anti-déprime, stimulant énergétique, prébiotique, anti-rachitique, anti-âge (vitamine B3), soutien prostatique, antiseptique",
    usage: "Frais (réfrigérateur/congélateur) ou sec réhydraté. Pain d'abeille = pollen fermenté",
    posology: "1 à 2 cuillères à soupe par jour, en cure d'un mois"
  },
  {
    id: "gelee-royale",
    name: "Gelée Royale",
    category: "apitherapy",
    keywords: ["fatigue", "convalescence", "immunité", "mémoire", "concentration", "vieillissement", "énergie", "récupération", "surmenage", "circulation"],
    indications: ["Fatigue sévère, surmenage, convalescence", "Baisse de concentration et mémoire", "Immunité faible", "Récupération post-maladie", "Soutien cardiovasculaire"],
    contraindications: ["Allergie aux produits de la ruche"],
    effects: "Revitalisante, immunostimulante, antioxydante, cardiovasculaire (augmente hémoglobine), anti-douleur (stimule endorphines)",
    usage: "Le matin à jeun, laisser fondre sous la langue",
    posology: "1 g par jour, cure de 3 semaines, pause 8 jours, 1-2 fois/an"
  },
  {
    id: "venin-abeille",
    name: "Venin d'Abeille",
    category: "apitherapy",
    keywords: ["rhumatisme", "arthrite", "inflammation", "douleur", "sclérose", "articulaire"],
    indications: ["Rhumatismes", "Douleurs articulaires chroniques", "Sclérose en plaques (accompagnement)", "Inflammations résistantes"],
    contraindications: ["Allergie aux piqûres d'abeilles (risque anaphylaxie)", "Ne jamais pratiquer seul", "Réservé aux professionnels"],
    effects: "Anti-inflammatoire puissant (mélittine), antalgique, cardiotonique",
    usage: "Piqûres localisées par professionnel ou injection médicale",
    posology: "Uniquement par professionnel expérimenté"
  }
];

// Fonction utilitaire pour obtenir toutes les ressources
export const getAllTreatmentResources = (): TreatmentResource[] => {
  return [
    ...aromatherapyData,
    ...gemmotherapyData,
    ...clayData,
    ...vegetableOilsData,
    ...hydrolatsData,
    ...apitherapyData
  ];
};

// Fonction pour trouver des ressources par mots-clés
export const findResourcesBySymptoms = (symptoms: string, maxResults: number = 3): TreatmentResource[] => {
  const symptomsLower = symptoms.toLowerCase();
  const allResources = getAllTreatmentResources();
  
  // Score chaque ressource basé sur le nombre de mots-clés matchés
  const scoredResources = allResources.map(resource => {
    let score = 0;
    
    // Vérifier les mots-clés
    resource.keywords.forEach(keyword => {
      if (symptomsLower.includes(keyword.toLowerCase())) {
        score += 2;
      }
    });
    
    // Vérifier les indications
    resource.indications.forEach(indication => {
      const indicationWords = indication.toLowerCase().split(/[\s,]+/);
      indicationWords.forEach(word => {
        if (word.length > 3 && symptomsLower.includes(word)) {
          score += 1;
        }
      });
    });
    
    return { resource, score };
  });
  
  // Filtrer et trier par score
  return scoredResources
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(item => item.resource);
};

// Fonction pour trouver des ressources par catégorie et symptômes
export const findResourcesByCategoryAndSymptoms = (
  symptoms: string, 
  category: TreatmentResource['category'],
  maxResults: number = 2
): TreatmentResource[] => {
  const symptomsLower = symptoms.toLowerCase();
  const categoryResources = getAllTreatmentResources().filter(r => r.category === category);
  
  const scoredResources = categoryResources.map(resource => {
    let score = 0;
    
    resource.keywords.forEach(keyword => {
      if (symptomsLower.includes(keyword.toLowerCase())) {
        score += 2;
      }
    });
    
    resource.indications.forEach(indication => {
      const indicationWords = indication.toLowerCase().split(/[\s,]+/);
      indicationWords.forEach(word => {
        if (word.length > 3 && symptomsLower.includes(word)) {
          score += 1;
        }
      });
    });
    
    return { resource, score };
  });
  
  return scoredResources
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(item => item.resource);
};
